import { t } from "@/lib/i18n";

export class ApiError extends Error {
    constructor(message: string, public status: number, public details?: unknown) { super(message); }
}

export const DEFAULT_TIMEOUT_MS = 20_000;

/** Fired once per 401 so the app can react to an expired session in one place
 * (redirect with context) instead of every mutation showing its own dead end. */
export const SESSION_EXPIRED_EVENT = "driverflow:session-expired";

function notifySessionExpired() {
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
}

export async function api<T>(url: string, options: RequestInit & { timeoutMs?: number } = {}): Promise<T> {
    const headers = new Headers(options.headers);
    if (options.body && !(options.body instanceof FormData))
        headers.set("Content-Type", "application/json");
    // A hung endpoint used to mean an eternal spinner with no way out.
    const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeoutMs);
    let response: Response;
    try {
        response = await fetch(url, {
            ...options,
            headers,
            credentials: "include",
            signal: options.signal ?? controller.signal,
        });
    } finally {
        window.clearTimeout(timer);
    }
    if (response.status === 204)
        return undefined as T;
    const contentType = response.headers.get("content-type") ?? "";
    const body = contentType.includes("application/json") ? await response.json() : await response.text();
    if (!response.ok) {
        if (response.status === 401 && !url.startsWith("/api/auth/")) notifySessionExpired();
        const message = typeof body === "object" && body?.error ? body.error : "spa_request_failed";
        throw new ApiError(t(message), response.status, typeof body === "object" ? body?.details : undefined);
    }
    return body as T;
}
export const jsonBody = (value: unknown): RequestInit => ({ body: JSON.stringify(value) });

/** fetch has no upload progress; big scans from mobile connections left the
 * dialog frozen on a spinner with zero feedback. */
export function uploadFile<T>(url: string, form: FormData, onProgress?: (percent: number) => void): Promise<T> {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", url);
        xhr.withCredentials = true;
        xhr.upload.onprogress = (event) => { if (event.lengthComputable && onProgress) onProgress(Math.round((event.loaded / event.total) * 100)); };
        xhr.onload = () => {
            let body: unknown = null;
            try { body = xhr.responseText ? JSON.parse(xhr.responseText) : null; } catch { body = xhr.responseText; }
            if (xhr.status >= 200 && xhr.status < 300) resolve(body as T);
            else {
                if (xhr.status === 401) notifySessionExpired();
                const record = body as { error?: string; details?: unknown } | null;
                reject(new ApiError(t(record?.error ?? "spa_request_failed"), xhr.status, record?.details));
            }
        };
        xhr.onerror = () => reject(new ApiError(t("spa_request_failed"), 0));
        xhr.ontimeout = () => reject(new ApiError(t("spa_request_failed"), 0));
        xhr.timeout = DEFAULT_TIMEOUT_MS * 4; // uploads legitimately take longer
        xhr.send(form);
    });
}
